import { defineComponent } from "@/utils/define-component";

type BISPayload = {
  a: string;
  platform: "shopify";
  variant_id: string;
  product_id: string;
  email?: string;
  phone?: string;
  country?: string;
  subscribe_for_sms?: boolean;
  source?: string;
  accepts_marketing?: boolean;
};

export default defineComponent(() => ({
  abortController: new AbortController(),
  closeTimeout: null as number | null,
  submitting: false as boolean,

  $refs: {
    form: undefined as HTMLFormElement | undefined,
    email: undefined as HTMLInputElement | undefined,
    publicKey: undefined as HTMLInputElement | undefined,
    productId: undefined as HTMLInputElement | undefined,
    variantId: undefined as HTMLInputElement | undefined,
    acceptsMarketing: undefined as HTMLInputElement | undefined,
    err: undefined as HTMLElement | undefined,
    ok: undefined as HTMLElement | undefined,
  },

  init() {
    const onChange = (e: Event) => {
      const target = e.target as Element | null;
      if (target?.closest?.("[x-data='productForm']")) this.syncVariantId();
    };
    document.addEventListener("change", onChange, {
      signal: this.abortController.signal,
    });

    this.syncVariantId();
  },

  destroy() {
    this.abortController.abort();
    if (this.closeTimeout) clearTimeout(this.closeTimeout);
  },

  currentVariantId(): Promise<string> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const input = document.querySelector<HTMLInputElement>(
          "[x-data='productForm'] input[name='id']",
        );

        resolve(input?.value ?? "");
      }, 1000);
    });
  },

  async syncVariantId() {
    if (this.$refs.variantId) {
      const v = await this.currentVariantId();
      if (v) this.$refs.variantId.value = v;
    }
  },

  showError(msg: string) {
    if (this.$refs.err) {
      this.$refs.err.textContent = msg;
      this.$refs.err.classList.remove("hidden");
    }
    this.$refs.ok?.classList.add("hidden");
  },

  showPending(msg = "Submitting…") {
    if (this.$refs.err) {
      this.$refs.err.textContent = msg;
      this.$refs.err.classList.remove("hidden");
    }
    this.$refs.ok?.classList.add("hidden");
  },

  showSuccess() {
    if (this.$refs.ok) {
      this.$refs.ok.classList.remove("hidden");
      this.$refs.ok.focus?.();
    }
    this.$refs.err?.classList.add("hidden");
  },

  klaviyoVariantId(numericVariantId: string) {
    return `$shopify:::$default:::${numericVariantId}`;
  },

  async submit() {
    if (this.submitting) return;
    const dialog = this.$root as HTMLDialogElement;

    const publicKey = this.$refs.publicKey?.value?.trim() || "";
    const email = this.$refs.email?.value?.trim() || "";
    const variantId = this.$refs.variantId?.value?.trim() || "";
    const productId = this.$refs.productId?.value?.trim() || "";
    const acceptsMarketing = !!this.$refs.acceptsMarketing?.checked;

    if (!publicKey) return this.showError("Missing Klaviyo public API key.");
    if (!email) return this.showError("Please enter a valid email.");
    if (!variantId) return this.showError("Missing variant id.");

    const url = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(
      publicKey,
    )}`;

    // const productGID = `gid://shopify/Product/${productId}`
    // const variantGID = `gid://shopify/ProductVariant/${variantId}`
    const catalogVariantId = `$shopify:::$default:::${variantId}`;

    const body = {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          channels: ["EMAIL"],
          profile: {
            data: {
              type: "profile",
              attributes: { email },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: catalogVariantId,
            },
          },
        },
      },
    };

    try {
      this.submitting = true;
      this.showPending();

      const res = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          revision: "2024-02-15",
        },
        credentials: "omit",
        body: JSON.stringify(body),
        signal: this.abortController.signal,
      });

      const data = await res.json().catch(() => ({}) as any);

      if (res.ok) {
        this.showSuccess();
        this.closeTimeout = window.setTimeout(() => {
          try {
            dialog?.close?.();
          } catch {}
          this.closeTimeout = null;
        }, 5000);

        this.$refs.form?.reset();
        this.syncVariantId();
      } else {
        const title =
          data?.errors?.[0]?.detail ||
          data?.detail ||
          "Sorry, something went wrong. Please try again.";
        this.showError(title);
      }
    } catch (err) {
      this.showError("Network error. Please try again.");
    } finally {
      this.submitting = false;
    }
  },
}));
